/**
 * Shared UI catalogs: labels, select options, and badge configs used across modules.
 */
import {
  AlertCircle,
  Archive,
  Award,
  BookOpen,
  Briefcase,
  Building,
  CheckCircle2,
  Circle,
  Clock,
  Cpu,
  FileSpreadsheet,
  FlaskConical,
  Globe,
  GraduationCap,
  Heart,
  HelpCircle,
  History,
  Hourglass,
  Info,
  Landmark,
  Library,
  LifeBuoy,
  MapPin,
  Mars,
  MinusCircle,
  PauseCircle,
  Puzzle,
  Scale,
  School,
  Ticket,
  Trophy,
  User,
  UserCircle,
  UserRoundCog,
  Users,
  UsersRound,
  Venus,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { BadgeVariant, StatusBadgeConfig } from '@/components/common/LabelBadge';
import type {
  AttendanceStatus,
  StudentAbsenceReason,
  TutorAbsenceReason,
} from '@/types/attendance.types';
import type { SessionTab as SessionInPersonTab, SessionType } from '@/types/inperson/session.types';
import type {
  SessionTab as SessionRemoteTab,
  SessionTypeMapping,
} from '@/types/remote/session.types';
import type { StudentSituation } from '@/types/remote/student.types';
import type { TutorSituation } from '@/types/remote/tutor.types';
import { type FolderSituation, type ProfileGender } from '@/types/user.types';

/** Linear email check: the label after `@` cannot consume the required `.`. */
export const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/;

/** `YYYY-YYYY` school-year code (e.g. `2025-2026`). */
export const SCHOOL_YEAR_CODE_PATTERN = /^\d{4}-\d{4}$/;

/** Yes / no {@link Select} options (`'true'` / `'false'` strings). */
export const BOOLEAN_SELECT_OPTIONS = [
  { value: 'true', label: 'Oui' },
  { value: 'false', label: 'Non' },
] as const;

/** Yes / no participation options (boolean + icon + color). */
export const BOOLEAN_PARTICIPATION_OPTIONS = [
  { value: true, label: 'Oui', icon: CheckCircle2, color: 'rgb(var(--status-success))' },
  { value: false, label: 'Non', icon: XCircle, color: 'rgb(var(--color-error))' },
] as const;

/** Tooltip shown beside the associative-engagement select. */
export const ASSOCIATIVE_ENGAGEMENT_TOOLTIP = 'UE engagement, points supplémentaires, ECTS, etc.';

/** Yes / no / don't-know {@link Select} options. */
export const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Oui' },
  { value: 'no', label: 'Non' },
  { value: 'dont-know', label: 'Ne sais pas' },
] as const;

/** Badge config for yes / no / don't-know. */
export const YES_NO_BADGE_CONFIG: Record<
  string,
  { label: string; icon: LucideIcon; color: string }
> = {
  yes: { label: 'Oui', icon: CheckCircle2, color: 'rgb(var(--status-success))' },
  no: { label: 'Non', icon: XCircle, color: 'rgb(var(--color-error))' },
  'dont-know': { label: 'Ne sais pas', icon: MinusCircle, color: 'rgb(var(--status-neutral))' },
};

/** Adult gender labels (`Homme` / `Femme` / `Non binaire`). */
export const GENDER_LABELS: Record<ProfileGender, string> = {
  male: 'Homme',
  female: 'Femme',
  other: 'Non binaire',
};

/** Adult gender {@link Select} options. */
export const GENDER_OPTIONS = [
  { value: 'male' as const, label: GENDER_LABELS.male },
  { value: 'female' as const, label: GENDER_LABELS.female },
  { value: 'other' as const, label: GENDER_LABELS.other },
] as const;

/** Adult gender icon + color. */
export const GENDER_APPEARANCE: Record<ProfileGender, { icon: LucideIcon; color: string }> = {
  male: { icon: Mars, color: 'rgb(var(--gender-male))' },
  female: { icon: Venus, color: 'rgb(var(--gender-female))' },
  other: { icon: HelpCircle, color: 'rgb(var(--status-neutral))' },
};

/** Adult gender {@link Select} options without `other`. */
export const GENDER_OPTIONS_WITHOUT_OTHER = GENDER_OPTIONS.filter(
  (option) => option.value !== 'other',
);

/** Student gender labels (`Garçon` / `Fille` / `Non binaire`). */
export const STUDENT_GENDER_LABELS: Record<ProfileGender, string> = {
  male: 'Garçon',
  female: 'Fille',
  other: 'Non binaire',
};

/** Student gender {@link Select} options. */
export const STUDENT_GENDER_OPTIONS = [
  { value: 'male' as const, label: STUDENT_GENDER_LABELS.male },
  { value: 'female' as const, label: STUDENT_GENDER_LABELS.female },
  { value: 'other' as const, label: STUDENT_GENDER_LABELS.other },
] as const;

/** Student gender {@link Select} options without `other`. */
export const STUDENT_GENDER_OPTIONS_WITHOUT_OTHER = STUDENT_GENDER_OPTIONS.filter(
  (option) => option.value !== 'other',
);

/** Civil title labels (`Monsieur` / `Madame`). */
export const CIVIL_TITLE_LABEL: Record<string, string> = {
  male: 'Monsieur',
  female: 'Madame',
};

/** Staff account-status badge config (`active` / `deactivated`). */
export const STAFF_STATUS_CONFIG: Record<string, StatusBadgeConfig> = {
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  deactivated: {
    label: 'Désactivé',
    icon: MinusCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Staff account-status {@link Select} options. */
export const STAFF_STATUS_FORM_OPTIONS = [
  { value: 'active', label: STAFF_STATUS_CONFIG.active.label },
  { value: 'deactivated', label: STAFF_STATUS_CONFIG.deactivated.label },
] as const;

/** Weekday filter chips (Mon–Fri short labels). */
export const WEEKDAY_FILTER_OPTIONS = [
  { value: 'monday', label: 'Lun' },
  { value: 'tuesday', label: 'Mar' },
  { value: 'wednesday', label: 'Mer' },
  { value: 'thursday', label: 'Jeu' },
  { value: 'friday', label: 'Ven' },
] as const;

/** Weekday key (`monday`…) → API day number (0 = Sunday). */
export const DAY_KEY_TO_AVAILABILITY_API: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

/** Date-range period tokens (all / today / week / month / year / custom). */
export const DATE_RANGE_PERIOD = {
  all: { value: 'all' as const, label: 'Tout' },
  today: { value: 'today' as const, label: "Aujourd'hui" },
  week: { value: 'week' as const, label: 'Cette semaine' },
  month: { value: 'month' as const, label: 'Ce mois' },
  year: { value: 'year' as const, label: 'Cette année' },
  custom: { value: 'custom' as const, label: 'Personnalisée' },
};

/** Date-range options: all / week / month / year. */
export const DATE_RANGE_PERIOD_OPTIONS = [
  DATE_RANGE_PERIOD.all,
  DATE_RANGE_PERIOD.week,
  DATE_RANGE_PERIOD.month,
  DATE_RANGE_PERIOD.year,
] as const;

/** Date-range options including today (no year). */
export const DATE_RANGE_PERIOD_OPTIONS_WITH_TODAY = [
  DATE_RANGE_PERIOD.all,
  DATE_RANGE_PERIOD.today,
  DATE_RANGE_PERIOD.week,
  DATE_RANGE_PERIOD.month,
] as const;

/** Date-range options: all / today / week / month / year. */
export const DATE_RANGE_PERIOD_OPTIONS_FULL = [
  DATE_RANGE_PERIOD.all,
  DATE_RANGE_PERIOD.today,
  DATE_RANGE_PERIOD.week,
  DATE_RANGE_PERIOD.month,
  DATE_RANGE_PERIOD.year,
] as const;

/** Date-range options including custom (no year). */
export const DATE_RANGE_PERIOD_OPTIONS_WITH_CUSTOM = [
  DATE_RANGE_PERIOD.all,
  DATE_RANGE_PERIOD.today,
  DATE_RANGE_PERIOD.week,
  DATE_RANGE_PERIOD.month,
  DATE_RANGE_PERIOD.custom,
] as const;

/** Shared assignable badge (boolean `true` and `'available'`). */
const ASSIGNABLE: StatusBadgeConfig = {
  label: 'Affectable',
  icon: CheckCircle2,
  badgeVariant: 'outlined',
  badgeColor: 'rgb(var(--status-success))',
};

/** Shared not-assignable badge (boolean `false` and `'unavailable'`). */
const NOT_ASSIGNABLE: StatusBadgeConfig = {
  label: 'Non affectable',
  icon: XCircle,
  badgeVariant: 'outlined',
  badgeColor: 'rgb(var(--color-error))',
};

/** Fallback assignability badge when the value is null / unknown. */
export const ASSIGNABILITY_UNKNOWN: StatusBadgeConfig = {
  label: '—',
  icon: AlertCircle,
  badgeVariant: 'ghost',
};

/** Assignability badge config keyed by boolean and `'available'` / `'unavailable'`. */
export const ASSIGNABILITY_CONFIG = {
  available: ASSIGNABLE,
  unavailable: NOT_ASSIGNABLE,
  true: ASSIGNABLE,
  false: NOT_ASSIGNABLE,
} as const;

/** Support-type labels (`Distanciel` / `Présentiel` / `Hybride`). */
export const SUPPORT_TYPE_LABEL: Record<string, string> = {
  remote: 'Distanciel',
  in_person: 'Présentiel',
  hybrid: 'Hybride',
};

/** Course-path labels (`Classique` / `Club de sport` / `Tous`). */
export const COURSE_PATH_LABEL: Record<string, string> = {
  standard: 'Classique',
  sport: 'Club de sport',
  all: 'Tous',
};

/** Course-path type (`standard` / `sport`). */
export type CoursePathType = 'standard' | 'sport';

/** Badge + icon config for a course path. */
export interface CoursePathTypeConfig {
  label: string;
  icon: LucideIcon;
}

/** Course-path badge config (classic / sport club). */
export const COURSE_PATH_TYPE_CONFIG: Record<CoursePathType, CoursePathTypeConfig> = {
  standard: {
    label: 'Classique',
    icon: GraduationCap,
  },
  sport: {
    label: 'Club de sport',
    icon: Trophy,
  },
};

/** Program types included in the in-person cascade filter. */
export const PROGRAM_CASCADE_IN_PERSON_TYPES = ['in_person', 'hybrid'] as const;
/** Program types included in the remote cascade filter. */
export const PROGRAM_CASCADE_REMOTE_TYPES = ['remote', 'hybrid'] as const;
/** Page size used by program cascade {@link Autocomplete}s. */
export const PROGRAM_CASCADE_PAGE_SIZE = 10;

/** Token name for an establishment-type badge color. */
export type EstablishmentColorName =
  | 'color-1'
  | 'color-2'
  | 'color-3'
  | 'color-4'
  | 'color-5'
  | 'color-6'
  | 'pending';

/** Badge + icon config for an establishment type. */
export interface EstablishmentTypeConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
  colorName: EstablishmentColorName;
}

/** Virtual "other" school id — not a catalog row. Matches mobile `establishmentId: "autre"`. */
export const OTHER_ESTABLISHMENT_ID = 'autre';

/** Display label for the virtual "other" school. */
export const OTHER_ESTABLISHMENT_LABEL = 'Autre';

/** Accepted aliases for the virtual "other" school (`autre` / `other`). */
export const OTHER_ESTABLISHMENT_IDS = ['autre', 'other'] as const;

/** UUID shape used by catalog establishment rows. */
export const CATALOG_ESTABLISHMENT_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Establishment-type badge config (school / collège / lycée / …). */
export const ESTABLISHMENT_TYPE_CONFIG: Record<string, EstablishmentTypeConfig> = {
  primaire: {
    label: 'École',
    icon: School,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
    colorName: 'color-2',
  },
  college: {
    label: 'Collège',
    icon: Building,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
    colorName: 'color-1',
  },
  lycee: {
    label: 'Lycée général',
    icon: GraduationCap,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-4-nuance-1)',
    colorName: 'color-4',
  },
  lycee_polyvalent: {
    label: 'Lycée polyvalent',
    icon: Library,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-5-nuance-3)',
    colorName: 'color-5',
  },
  professionnel: {
    label: 'Lycée professionnel',
    icon: Briefcase,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-1)',
    colorName: 'color-3',
  },
  other: {
    label: 'Autre',
    icon: HelpCircle,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-5-nuance-1)',
    colorName: 'color-5',
  },
  university: {
    label: 'Université',
    icon: Landmark,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-6-nuance-1)',
    colorName: 'color-6',
  },
  engineering_school: {
    label: "École d'ingénieurs",
    icon: Cpu,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-pending))',
    colorName: 'pending',
  },
};

/** Establishment type → school-cycle codes (`primary` / `middle` / `high`). */
export const ESTABLISHMENT_TYPE_TO_CYCLE: Record<string, string[]> = {
  primaire: ['primary'],
  college: ['middle'],
  lycee: ['high'],
  lycee_polyvalent: ['high'],
  professionnel: ['high'],
};

/** In-person exit-reason {@link Select} options. */
export const INPERSON_EXIT_REASON_OPTIONS = [
  { value: 'early_exit', label: "Fin d'engagement anticipé" },
  { value: 'reassignment', label: 'Réaffectation dans une autre zone' },
  { value: 'no_follow_up', label: 'Ne donne pas suite' },
  { value: 'other', label: 'Autre' },
] as const;

/** In-person exit-reason labels keyed by code. */
export const INPERSON_EXIT_REASON_LABEL: Record<string, string> = Object.fromEntries(
  INPERSON_EXIT_REASON_OPTIONS.map((o) => [o.value, o.label]),
);

/** Stored French labels → in-person exit-reason codes. */
export const INPERSON_EXIT_REASON_LEGACY_MAP: Record<string, string> = {
  "Fin d'engagement anticipé": 'early_exit',
  'Réaffectation dans une autre zone': 'reassignment',
  'Ne donne pas suite': 'no_follow_up',
  Autre: 'other',
};

/** Remote archive-reason {@link Select} options. */
export const ARCHIVE_REASON_OPTIONS = [
  { value: 'no_follow_up', label: 'Ne donne pas suite' },
  { value: 'disengaged', label: 'Se désengage' },
  { value: 'pause', label: 'Pause' },
  { value: 'duplicate', label: 'Doublon' },
  { value: 'other', label: 'Autre' },
] as const;

/** Remote archive-reason labels keyed by code. */
export const ARCHIVE_REASON_LABEL: Record<string, string> = Object.fromEntries(
  ARCHIVE_REASON_OPTIONS.map((o) => [o.value, o.label]),
);

/** Events-and-summaries page tab (`events` / `summaries`). */
export type EventsAndSummariesTab = 'events' | 'summaries';

/** UI labels for events-and-summaries tabs. */
export const EVENTS_AND_SUMMARIES_TAB_LABELS: Record<string, string> = {
  events: 'Événements',
  summaries: 'Synthèses',
};

/** Tab colors for events-and-summaries. */
export const EVENTS_AND_SUMMARIES_TAB_COLORS: Record<string, string> = {
  events: 'var(--color-2-nuance-1)',
  summaries: 'var(--color-3-nuance-1)',
};

/** Tab icons for events-and-summaries. */
export const EVENTS_AND_SUMMARIES_TAB_ICONS = {
  events: Ticket,
  summaries: FileSpreadsheet,
} as const;

/** Folder-situation badge config (incomplete / partial / in validation / complete). */
export const FOLDER_SITUATION_CONFIG: Record<FolderSituation, StatusBadgeConfig> = {
  incomplete: {
    label: 'Incomplet',
    icon: AlertCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
  partial: {
    label: 'Partiel',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  in_validation: {
    label: 'En validation',
    icon: Hourglass,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  complete: {
    label: 'Complet',
    icon: CheckCircle2,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-success))',
  },
};

/** Onboarding-step completion badge config (empty / partial / complete). */
export const STEP_COMPLETION_CONFIG = {
  empty: {
    label: 'Vide',
    icon: Circle,
    badgeVariant: 'outlined' as const,
    badgeColor: 'rgb(var(--color-error))',
  },
  partial: {
    label: 'Partiel',
    icon: Clock,
    badgeVariant: 'outlined' as const,
    badgeColor: 'rgb(var(--status-warning))',
  },
  complete: {
    label: 'Complet',
    icon: CheckCircle2,
    badgeVariant: 'active' as const,
    badgeColor: undefined,
  },
};

/** Registration-source badge config (landing, manager, event, …). */
export const REGISTRATION_SOURCE_CONFIG: Record<string, StatusBadgeConfig> = {
  'Landing Page': {
    label: 'Landing Page',
    icon: Globe,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-info))',
  },
  'Gestion des tuteurs': {
    label: 'Gestion interne',
    icon: UserCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-2-nuance-1))',
  },
  'Événement terrain': {
    label: 'Événement terrain',
    icon: MapPin,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  self_registration: {
    label: 'Auto-inscription',
    icon: Globe,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-info))',
  },
  manager_import: {
    label: 'Import gestionnaire',
    icon: UserCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-2-nuance-1))',
  },
  manager: {
    label: 'Gestionnaire',
    icon: UserCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-2-nuance-1))',
  },
  event: {
    label: 'Événement terrain',
    icon: MapPin,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
};

/** Fallback registration-source badge when the stored value is unknown. */
export const DEFAULT_SOURCE_CONFIG: StatusBadgeConfig = {
  label: '?',
  icon: UserCircle,
  badgeVariant: 'ghost',
};

/** Token name for a pair-type detail color. */
export type PairTypeDetailColor = 'color-2' | 'color-1' | 'color-3' | 'error';

/** Badge + icon config for a pair type. */
export interface PairTypeConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
  detailColorName: PairTypeDetailColor;
}

/** Pair-type badge config (manual / binôme cœur / balanced / history). */
export const PAIR_TYPE_CONFIG: Record<string, PairTypeConfig> = {
  manual: {
    label: 'Manuel',
    icon: UserRoundCog,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-2-nuance-1)',
    detailColorName: 'color-2',
  },
  auto_binome_coeur: {
    label: 'Binôme Cœur',
    icon: Heart,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--accent-red-core))',
    detailColorName: 'error',
  },
  auto_balanced: {
    label: 'Équilibré',
    icon: Scale,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-1-nuance-1)',
    detailColorName: 'color-1',
  },
  auto_history: {
    label: 'Historique',
    icon: History,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-3-nuance-1)',
    detailColorName: 'color-3',
  },
};

/** In-person professional-situation {@link Select} options (student / high_school_student / other). */
export const INPERSON_PRO_SITUATION_OPTIONS = [
  { value: 'student', label: 'Étudiant' },
  { value: 'high_school_student', label: 'Lycéen' },
  { value: 'other', label: 'Autre' },
] as const;

/** All in-person professional-situation labels including legacy values (employee / retired). */
export const INPERSON_PRO_SITUATION_LABEL: Record<string, string> = {
  student: 'Étudiant',
  high_school_student: 'Lycéen',
  other: 'Autre',
  employee: 'Salarié',
  retired: 'Retraité',
};

/** Catalog options plus the current legacy employee/retired value so an existing fiche still displays. */
export function inPersonProSituationSelectOptions(
  currentStatus?: string,
): ReadonlyArray<{ value: string; label: string }> {
  if (currentStatus !== 'employee' && currentStatus !== 'retired') {
    return INPERSON_PRO_SITUATION_OPTIONS;
  }
  const label = INPERSON_PRO_SITUATION_LABEL[currentStatus];
  if (!label) return INPERSON_PRO_SITUATION_OPTIONS;
  return [{ value: currentStatus, label }, ...INPERSON_PRO_SITUATION_OPTIONS];
}

/** Remote professional-situation status {@link Select} options. */
export const PRO_SITUATION_STATUS_OPTIONS = [
  { value: 'high_school_student', label: 'Lycéens' },
  { value: 'student', label: 'Étudiants' },
  { value: 'employee', label: 'Employés' },
  { value: 'entrepreneur', label: "Entrepreneurs, artisans et commerçants, chef d'entreprise" },
  { value: 'civil_servant', label: 'Fonctionnaires' },
  { value: 'unemployed', label: 'Sans activité / Sans emploi' },
  { value: 'retired', label: 'Retraités' },
  { value: 'executive', label: 'Cadres et professions intellectuelles supérieures' },
  { value: 'intermediate', label: 'Professions intermédiaires' },
  { value: 'worker', label: 'Ouvriers' },
  { value: 'farmer', label: 'Agriculteurs exploitants' },
] as const;

/** Remote professional-situation status labels keyed by code. */
export const PRO_SITUATION_STATUS_LABEL: Record<string, string> = Object.fromEntries(
  PRO_SITUATION_STATUS_OPTIONS.map((o) => [o.value, o.label]),
);

/** CamelCase / alias pro-situation values → catalog codes. */
export const PRO_SITUATION_STATUS_LEGACY_MAP: Record<string, string> = {
  professional: 'employee',
  highSchoolStudent: 'high_school_student',
  civilServant: 'civil_servant',
  executives: 'executive',
  intermediateProfessions: 'intermediate',
  workers: 'worker',
  farmers: 'farmer',
};

/** Remote professional-situation badge colors keyed by status code. */
export const PRO_SITUATION_COLOR_MAP: Record<string, string> = {
  high_school_student: 'var(--color-3-nuance-1)',
  student: 'rgb(var(--color-info))',
  employee: 'rgb(var(--status-success))',
  entrepreneur: 'var(--color-1-nuance-1)',
  civil_servant: 'var(--color-2-nuance-1)',
  executive: 'rgb(var(--accent-purple))',
  intermediate: 'rgb(var(--status-warning))',
  worker: 'rgb(var(--status-pending))',
  farmer: 'rgb(var(--status-success))',
  retired: 'rgb(var(--status-neutral))',
  inactive: 'rgb(var(--status-neutral))',
};

/** High-school curriculum {@link Select} options (general / technological / vocational). */
export const HIGH_SCHOOL_CURRICULUM_OPTIONS = [
  { value: 'general', label: 'Général' },
  { value: 'technological', label: 'Technologique' },
  { value: 'vocational', label: 'Professionnel' },
] as const;

/** High-school curriculum labels keyed by code. */
export const HIGH_SCHOOL_CURRICULUM_LABEL: Record<string, string> = Object.fromEntries(
  HIGH_SCHOOL_CURRICULUM_OPTIONS.map((o) => [o.value, o.label]),
);

/** Stored French / mixed-case labels → high-school curriculum codes. */
export const HIGH_SCHOOL_CURRICULUM_LEGACY_MAP: Record<string, string> = {
  Général: 'general',
  général: 'general',
  Technologique: 'technological',
  technologique: 'technological',
  Professionnel: 'vocational',
  professionnel: 'vocational',
};

/** Study-level {@link Select} options (`bac` … `bac-5` / `other`). */
export const STUDY_LEVEL_OPTIONS = [
  { value: 'bac', label: 'Bac' },
  { value: 'bac-1', label: 'Bac+1' },
  { value: 'bac-2', label: 'Bac+2' },
  { value: 'bac-3', label: 'Bac+3' },
  { value: 'bac-4', label: 'Bac+4' },
  { value: 'bac-5', label: 'Bac+5' },
  { value: 'other', label: 'Autre' },
] as const;

/** Study-level labels keyed by code. */
export const STUDY_LEVEL_LABEL: Record<string, string> = Object.fromEntries(
  STUDY_LEVEL_OPTIONS.map((o) => [o.value, o.label]),
);

/** Tutoring-course {@link Select} options (sciences, lettres, STAPS, …). */
export const TUTORING_COURSE_OPTIONS = [
  { value: 'sciences-et-technologies', label: 'Sciences et technologies' },
  { value: 'lettres-les-langues-lart', label: "Lettres, les langues, l'art" },
  { value: 'sciences-humaines-et-sociales', label: 'Sciences humaines et sociales' },
  { value: 'droit-et-sciences-politiques', label: 'Droit et sciences politiques' },
  { value: 'economie-et-gestion', label: 'Économie et gestion' },
  { value: 'sante', label: 'Santé' },
  { value: 'sport', label: 'Sport' },
  { value: 'staps', label: 'STAPS' },
  { value: 'sciences-de-leducation', label: "Sciences de l'éducation" },
  { value: 'sciences-de-la-vie-et-de-la-terre', label: 'Sciences de la vie et de la terre' },
  { value: 'sciences-pour-la-sante', label: 'Sciences pour la santé' },
  { value: 'sciences-pour-lingenieur', label: "Sciences pour l'ingénieur" },
  { value: 'sciences-du-langage', label: 'Sciences du langage' },
  { value: 'sociologie', label: 'Sociologie' },
  { value: 'communication-marketing', label: 'Communication/Marketing' },
  { value: 'commerce', label: 'Commerce' },
  { value: 'eco-gestion-administration', label: 'Éco-gestion/Administration' },
  { value: 'mathematique-informatique', label: 'Mathématique/Informatique' },
  { value: 'histoire-geographie', label: 'Histoire/Géographie' },
  { value: 'psychologie', label: 'Psychologie' },
  { value: 'journalisme', label: 'Journalisme' },
  { value: 'autres', label: 'Autres' },
] as const;

/** Tutoring-course labels keyed by code. */
export const TUTORING_COURSE_LABEL: Record<string, string> = Object.fromEntries(
  TUTORING_COURSE_OPTIONS.map((o) => [o.value, o.label]),
);

/** Activity-sector {@link Select} options (INSEE-style buckets). */
export const ACTIVITY_SECTOR_OPTIONS = [
  { value: 'agriculture-forestry-fishing', label: 'Agriculture, sylviculture et pêche' },
  { value: 'industry', label: 'Industrie' },
  { value: 'construction', label: 'Construction' },
  { value: 'commerce-repair', label: 'Commerce, réparation' },
  { value: 'transport-storage', label: 'Transports et entreposage' },
  { value: 'accommodation-catering', label: 'Hébergement et restauration' },
  { value: 'information-communication', label: 'Information et communication' },
  { value: 'financial-insurance', label: "Activités financières et d'assurance" },
  { value: 'real-estate', label: 'Immobilier' },
  { value: 'scientific-technical', label: 'Activités scientifiques et techniques' },
  { value: 'administrative-support', label: 'Activités de services administratifs et de soutien' },
  { value: 'public-administration', label: 'Administration publique' },
  { value: 'education', label: 'Enseignement' },
  { value: 'health-social', label: 'Santé humaine et action sociale' },
  { value: 'arts-entertainment', label: 'Arts, spectacles et activités récréatives' },
  { value: 'other-services', label: 'Autres activités de services' },
  { value: 'other', label: 'Autre' },
] as const;

/** Activity-sector labels keyed by code. */
export const ACTIVITY_SECTOR_LABEL: Record<string, string> = Object.fromEntries(
  ACTIVITY_SECTOR_OPTIONS.map((o) => [o.value, o.label]),
);

/** In-person tutor field-of-activity {@link Select} options. */
export const FIELD_OF_ACTIVITY_TUTOR_OPTIONS = [
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'commerce', label: 'Commerce' },
  { value: 'industry', label: 'Industrie' },
  { value: 'service', label: 'Service' },
  { value: 'education', label: 'Éducation' },
  { value: 'health', label: 'Santé' },
  { value: 'transport', label: 'Transport' },
] as const;

/** In-person tutor field-of-activity labels keyed by code. */
export const FIELD_OF_ACTIVITY_TUTOR_LABEL: Record<string, string> = Object.fromEntries(
  FIELD_OF_ACTIVITY_TUTOR_OPTIONS.map((o) => [o.value, o.label]),
);

/** Civil-service branch {@link Select} options (FPE / FPT / FPH). */
export const CIVIL_SERVICE_BRANCH_OPTIONS = [
  { value: 'FPE', label: "Fonction publique de l'État (FPE)" },
  { value: 'FPT', label: 'Fonction publique territoriale (FPT)' },
  { value: 'FPH', label: 'Fonction publique hospitalière (FPH)' },
] as const;

/** Civil-service branch labels keyed by code. */
export const CIVIL_SERVICE_BRANCH_LABEL: Record<string, string> = Object.fromEntries(
  CIVIL_SERVICE_BRANCH_OPTIONS.map((o) => [o.value, o.label]),
);

/** Badge + icon config for a remote person status / situation. */
export interface RemotePersonStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Remote tutor situation badge config. */
export const TUTOR_SITUATION_CONFIG: Record<TutorSituation, RemotePersonStatusConfig> = {
  registration_in_progress: {
    label: "En cours d'inscription",
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-pending))',
  },
  awaiting_validation: {
    label: 'En attente validation',
    icon: Hourglass,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
  awaiting_certification: {
    label: 'En attente certification',
    icon: Award,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-1)',
  },
  awaiting_student: {
    label: "En attente d'élève",
    icon: Users,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  tutoring_in_progress: {
    label: 'Tutorat en cours',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  stop: {
    label: 'En pause',
    icon: PauseCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
};

/** Remote student situation badge config. */
export const STUDENT_SITUATION_CONFIG: Record<StudentSituation, RemotePersonStatusConfig> = {
  registration_in_progress: {
    label: "En cours d'inscription",
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-pending))',
  },
  awaiting_tutor: {
    label: 'En attente tuteur',
    icon: Users,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  tutoring_in_progress: {
    label: 'Tutorat en cours',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  stop: {
    label: 'En pause',
    icon: PauseCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-warning))',
  },
};

/** Remote student list category (`pre_registered` / `student`). */
export type StudentCategory = 'pre_registered' | 'student';

/** Remote pre-registered student status. */
export type StudentPendingStatus = 'pending' | 'archived' | 'deleted';

/** Remote active-student status (with/without tutor, suspended, archived, deleted). */
export type StudentActiveStatus =
  | 'active_without_tutor'
  | 'active_with_tutor'
  | 'suspended'
  | 'archived'
  | 'deleted';

/** Badge color for pending person statuses. */
export const PERSON_PENDING_STATUS_COLOR = 'var(--color-2-nuance-1)';
/** Badge color for suspended person statuses. */
export const PERSON_SUSPENDED_STATUS_COLOR = 'rgb(var(--status-pending))';

/** Remote pre-registered student status badge config. */
export const STUDENT_PENDING_STATUS_CONFIG: Record<StudentPendingStatus, RemotePersonStatusConfig> =
  {
    pending: {
      label: 'En attente',
      icon: Clock,
      badgeVariant: 'outlined',
      badgeColor: PERSON_PENDING_STATUS_COLOR,
    },
    archived: {
      label: 'Archivé',
      icon: Archive,
      badgeVariant: 'ghost',
      badgeColor: 'rgb(var(--color-error))',
    },
    deleted: { label: 'Supprimé', icon: XCircle, badgeVariant: 'disabled' },
  };

/** Remote active-student status badge config. */
export const STUDENT_ACTIVE_STATUS_CONFIG: Record<StudentActiveStatus, RemotePersonStatusConfig> = {
  active_without_tutor: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  active_with_tutor: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  suspended: {
    label: 'Suspendu',
    icon: PauseCircle,
    badgeVariant: 'outlined',
    badgeColor: PERSON_SUSPENDED_STATUS_COLOR,
  },
  archived: {
    label: 'Archivé',
    icon: Archive,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--color-error))',
  },
  deleted: { label: 'Supprimé', icon: XCircle, badgeVariant: 'disabled' },
};

/** School-situation badge config (primary / middle / high school). */
export const SCHOOL_SITUATION_CONFIG: Record<string, RemotePersonStatusConfig> = {
  primary: {
    label: 'Élève du primaire',
    icon: GraduationCap,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-1-nuance-1))',
  },
  middle_school: {
    label: 'Collégien',
    icon: GraduationCap,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-2-nuance-1))',
  },
  high_school: {
    label: 'Lycéen',
    icon: GraduationCap,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-3-nuance-1))',
  },
};

/** Display order for in-person tutor subject pickers (POC-716 / `displayOrder=presentiel`). */
export const INPERSON_SUBJECT_ORDER = [
  'francais',
  'mathematiques',
  'histoire',
  'anglais',
  'espagnol',
  'physique-chimie',
  'svt',
] as const;

/** Canonical slug for an in-person tutor subject picker priority entry. */
export type InpersonSubjectSlug = (typeof INPERSON_SUBJECT_ORDER)[number];

/** Alternate slugs that map to a canonical in-person subject order entry. */
export const INPERSON_SUBJECT_ALIASES: Record<string, InpersonSubjectSlug> = {
  maths: 'mathematiques',
  mathematique: 'mathematiques',
  'histoire-geo': 'histoire',
  'histoire-geographie': 'histoire',
  'histoire-geographie-emc': 'histoire',
  'sciences-de-la-vie-et-de-la-terre': 'svt',
  'sciences-de-la-vie': 'svt',
};

/** Remote subject-preference {@link Select} options. */
export const REMOTE_SUBJECT_OPTIONS = [
  { value: 'scientific-and-first', label: 'Matières scientifiques en priorité' },
  { value: 'literary-and-first', label: 'Matières littéraires en priorité' },
  { value: 'both-subjects', label: 'Un peu de tout !' },
] as const;

/** Remote subject-preference badge config. */
export const REMOTE_SUBJECT_CONFIG: Record<string, StatusBadgeConfig> = {
  'scientific-and-first': {
    label: 'Matières scientifiques en priorité',
    icon: FlaskConical,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-2)',
  },
  'literary-and-first': {
    label: 'Matières littéraires en priorité',
    icon: BookOpen,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  'both-subjects': {
    label: 'Un peu de tout !',
    icon: GraduationCap,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-2)',
  },
};

/** Badge + icon config for a session type. */
export interface SessionTypeConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
  colorName: 'color-1' | 'color-2' | 'color-3' | 'color-4' | 'color-5';
}

/** In-person session-type badge config. */
export const SESSION_TYPE_CONFIG: Record<string, SessionTypeConfig> = {
  homework_help: {
    label: 'Aide aux devoirs',
    icon: BookOpen,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-2-nuance-1)',
    colorName: 'color-2',
  },
  individual_tutoring: {
    label: 'Tutorat individuel',
    icon: User,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-4-nuance-2)',
    colorName: 'color-4',
  },
  workshop: {
    label: 'Atelier',
    icon: Puzzle,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-3-nuance-2)',
    colorName: 'color-3',
  },
  whole_class: {
    label: 'Accompagnement en classe',
    icon: GraduationCap,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-5-nuance-3)',
    colorName: 'color-5',
  },
  group_tutoring: {
    label: 'Tutorat de groupe',
    icon: UsersRound,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-1-nuance-2)',
    colorName: 'color-1',
  },
  quick_help_desk: {
    label: "Bureau d'aide rapide",
    icon: LifeBuoy,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-3-nuance-1)',
    colorName: 'color-3',
  },
  information_meeting_in_person: {
    label: "Réunion d'information",
    icon: Info,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-2-nuance-1)',
    colorName: 'color-2',
  },
};

/** In-person session-type keys shown in the type filter. */
export const SESSION_TYPE_FILTER_KEYS = [
  'homework_help',
  'individual_tutoring',
  'workshop',
  'whole_class',
  'group_tutoring',
  'quick_help_desk',
  'information_meeting_in_person',
] as const;

/** Remote session-type badge config. */
export const SESSION_REMOTE_TYPE_CONFIG: Record<string, SessionTypeConfig> = {
  individual_tutoring: {
    label: 'Tutorat individuel',
    icon: User,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-1-nuance-1)',
    colorName: 'color-1',
  },
  information_meeting: {
    label: "Réunion d'information",
    icon: Info,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-2-nuance-1)',
    colorName: 'color-2',
  },
  welcome_meeting: {
    label: "Réunion d'accueil",
    icon: Users,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-3-nuance-1)',
    colorName: 'color-3',
  },
  workshop: {
    label: 'Événement terrain',
    icon: MapPin,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--accent-purple))',
    colorName: 'color-2',
  },
};

/** UI labels for remote session tabs. */
export const SESSION_TAB_LABELS: Record<SessionRemoteTab, string> = {
  all: 'Tout',
  tutoring: 'Tutorat',
  'information-meeting': "Réunion d'information",
  'welcome-meeting': "Réunion d'accueil",
  'field-event': 'Événement terrain',
};

/** UI labels for {@link SessionInPersonTab}. */
export const SESSION_IN_PERSON_TAB_LABELS: Record<SessionInPersonTab, string> = {
  tutoring: 'Tutorat',
  'information-meeting': "Réunion d'information",
};

/** Session types included in each in-person tab. */
export const SESSION_TAB_TO_TYPES: Record<SessionInPersonTab, SessionType[]> = {
  tutoring: [
    'individual_tutoring',
    'homework_help',
    'group_tutoring',
    'workshop',
    'whole_class',
    'quick_help_desk',
  ],
  'information-meeting': ['information_meeting_in_person'],
};

/**
 * Tutorat tab: Individual remote tutoring sessions only
 */
const TUTORING_TYPES: Record<'individual', SessionTypeMapping> = {
  individual: {
    type: 'individual_tutoring',
    sessionFormat: 'remote',
    workflow: 'tutorat_remote',
    label: 'Tutorat individuel',
    icon: 'User',
  },
};

/**
 * Information meeting (RI)
 */
const INFORMATION_MEETING_TYPE: SessionTypeMapping = {
  type: 'information_meeting',
  sessionFormat: 'remote',
  workflow: 'recrutement_remote',
  label: "Réunion d'information",
  icon: 'Info',
};

/**
 * Welcome meeting (RA)
 */
const WELCOME_MEETING_TYPE: SessionTypeMapping = {
  type: 'welcome_meeting',
  sessionFormat: 'remote',
  workflow: 'recrutement_remote',
  label: "Réunion d'accueil",
  icon: 'Users',
};

/**
 * Field events
 * Backend type: 'workshop', in_person format, 'terrain' workflow
 */
const FIELD_EVENT_TYPE: SessionTypeMapping = {
  type: 'workshop',
  sessionFormat: 'in_person',
  workflow: 'terrain',
  label: 'Événement terrain',
  icon: 'MapPin',
};

/**
 * Complete mapping from tab to session type(s)
 */
export const SESSION_TAB_TYPE_MAP: Record<
  SessionRemoteTab,
  SessionTypeMapping | SessionTypeMapping[]
> = {
  all: [],
  tutoring: [TUTORING_TYPES.individual],
  'information-meeting': INFORMATION_MEETING_TYPE,
  'welcome-meeting': WELCOME_MEETING_TYPE,
  'field-event': FIELD_EVENT_TYPE,
};

/** Tutor absence-reason labels. */
export const TUTOR_ABSENCE_REASONS: Record<TutorAbsenceReason, string> = {
  personal_emergency: 'Imprévu personnel',
  professional_emergency: 'Imprévu professionnel',
  leave: 'Congé',
  technical_issue: 'Problème technique',
  mutual_agreement: 'Commun accord',
  other: 'Autre',
} as const;

/** Student absence-reason labels. */
export const STUDENT_ABSENCE_REASONS: Record<StudentAbsenceReason, string> = {
  personal_emergency: 'Imprévu personnel',
  illness: 'Maladie',
  technical_issue: 'Problème technique',
  mutual_agreement: 'Commun accord',
  other: 'Autre',
} as const;

/** Attendance-status labels (`present` / `absent` / `excused` / `pending`). */
export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: 'Présent',
  absent: 'Absent',
  excused: 'Excusé',
  pending: 'En attente',
} as const;

/** React Query keys for the inbox list and unread count. */
export const NOTIFICATION_QUERY_KEYS = {
  all: ['notifications'] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
  infinite: (pageSize: number, unreadOnly: boolean) =>
    ['notifications', 'infinite', { pageSize, unreadOnly }] as const,
};
